import React, { useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { UiControlPropsType } from 'shared/types';
import { useMedia, useObservable, useOnClickOutside } from 'shared/hooks';
import { COLORS } from 'shared/constants';

import { UiIcon } from '../UiIcon';
import { UiScrollbar } from '../UiScrollbar';
import { UiModal } from '../UiModal';
import { UiSlot } from '../UiSlot';

import './styles.scss';

type OptionType = {
    id: number | null;
    name: string;
} | {
    id: string | null;
    name: string;
};

type PropsType<T extends OptionType> = UiControlPropsType<T['id'] | null> & {
    styles?: {
        items?: React.CSSProperties;
    };
    items: T[];
    isDisabled?: boolean;
    placeholder?: string;
    scrollIntoView?: boolean;
    withSearch?: boolean;
    searchPlaceholder?: string;
    clearSearchOnClose?: boolean;
    slots?: {
        control?: (data: { item?: T; onClick: () => void; isOpened: boolean }) => React.ReactNode;
        controlValue?: (data: { item?: T, placeholder: string, value: string | number }) => React.ReactNode;
        itemValue?: (data: { item: T }) => React.ReactNode;
    };
};

export const UiSelect = observer(function UiSelect<T extends OptionType>({
    className,
    style,
    styles,
    scrollIntoView = false,
    name = 'ui-new-select',
    items,
    slots,
    placeholder,
    isDisabled,
    withSearch = false,
    searchPlaceholder = 'Поиск',
    clearSearchOnClose = false,
    onChange = () => undefined,
    value,
}: PropsType<T>) {
    const ref = useRef<HTMLDivElement>(null);
    const refOuter = useRef<HTMLDivElement>(null);

    const { isDesktop } = useMedia();

    useOnClickOutside(ref, () => {
        if (!isDesktop) {
            return;
        }
        close();
    });

    const store = useObservable({
        isOpened: false,
        search: '',
    });

    const item = items.find(item => item.id === value);
    const filteredItems = useMemo(() => {
        const search = store.search.trim().toLocaleLowerCase();

        if (!search) {
            return items;
        }

        return items.filter(item => item.name.toLocaleLowerCase().includes(search));
    }, [items, store.search]);

    const close = () => {
        store.update({
            isOpened: false,
            search: clearSearchOnClose ? '' : store.search,
        });
    };

    const handleClick = () => {
        if (store.isOpened) {
            close();
            return;
        }

        store.set('isOpened', true);
        if (scrollIntoView) {
            setTimeout(() => {
                refOuter?.current?.scrollIntoView({
                    behavior: 'smooth',
                });
            }, 100);
        }
    };

    const handleSelect = (id: T['id'] | null) => {
        onChange({
            name, value: id,
        });
        close();
    };

    const renderSearch = () => withSearch && (
        <div className="ui-new-select-search">
            <UiIcon.Search size={16} className="ui-new-select-search__icon" />
            <input
                className="ui-new-select-search__control"
                type="text"
                placeholder={searchPlaceholder}
                value={store.search}
                onChange={e => store.set('search', e.target.value)}
            />
        </div>
    );

    const renderItems = () => (
        <>
            {filteredItems.map(item => (
                <div
                    key={item.id}
                    className={classnames('ui-new-select-item', {
                        'ui-new-select-item--selected': item.id === value,
                    })}
                    onClick={() => handleSelect(item.id)}
                >
                    <UiSlot
                        template={slots?.itemValue?.({ item })}
                        render={<span>{item.name}</span>}
                    />
                    {item.id === value && <UiIcon.CircleCheckIcon className='ui-new-select-item__check' size={16} fill={COLORS._787B83} color={COLORS._FFFFFF} />}
                </div>
            ))}
            {filteredItems.length === 0 && (
                <div className="ui-new-select__empty">Ничего не найдено</div>
            )}
        </>
    );

    return (
        <div
            ref={ref}
            className={classnames('ui-new-select', className, {
                'ui-new-select--opened': store.isOpened,
                'ui-new-select--disabled': isDisabled,
                'ui-new-select--controlled': !!slots?.control,
                'ui-new-select--selected': !!item,
                'ui-new-select--searchable': withSearch,
            })}
            style={style}
        >
            <UiSlot
                template={slots?.control?.({
                    item,
                    onClick: handleClick,
                    isOpened: store.isOpened,
                })}
                render={(
                    <div className="ui-new-select-control ui-new-select-control--validation" onClick={handleClick}>
                        <div className={'ui-new-select-control__value'}>
                            <UiSlot
                                template={slots?.controlValue?.({
                                    item,
                                    placeholder: placeholder || 'Не выбрано',
                                    value: item?.name || placeholder || 'Не выбрано',
                                })}
                                render={item?.name || placeholder || 'Не выбрано'}
                            />
                        </div>
                        <UiIcon.ChevronDown className={'ui-new-select-control__chevron'} size={16} />
                    </div>
                )}
            />
            {isDesktop && (
                <div ref={refOuter} className="ui-new-select__items" style={styles?.items}>
                    {renderSearch()}
                    <UiScrollbar
                        maxHeight={270}
                        scrollbarGap={withSearch ? [0, 0] : undefined}
                    >
                        {renderItems()}
                    </UiScrollbar>
                </div>
            )}
            {!isDesktop && (
                <UiModal
                    isOpened={store.isOpened}
                    onClose={close}
                >
                    <div className="ui-new-select__items">
                        {renderSearch()}
                        <UiScrollbar maxHeight={270} scrollbarGap={[0, 0]}>
                            {renderItems()}
                        </UiScrollbar>
                    </div>
                </UiModal>
            )}
        </div>
    );
});
